import { Box, Dialog, DialogContent, Grid } from "@mui/material";
import { useState } from "react";
import ObjectiveSelectionReviewModal from "./ObjectiveSelectionReviewModal";

import type { OASelectionV2, ObjetivoAprendizajeV2 } from "./types";

import ObjectiveSelectHeader from "./ObjectiveSelectHeader";
import ObjectiveSelectFooter from "./ObjectiveSelectFooter";
import ObjectiveSelectRightPanel from "./ObjectiveSelectRightPanel";
import ObjectiveSelectLeftTree from "./ObjectiveSelectLeftTree";

import { useOATreeLoaderTP } from "./useOATreeLoaderTP";
import { useOATreeSelection } from "./useOATreeSelection";

interface Props {
  open: boolean;
  objetivo: ObjetivoAprendizajeV2;
  initialSelection?: OASelectionV2;
  onClose: () => void;
  onConfirm: (selection: OASelectionV2) => void;
}

const ObjectiveSelectModal = ({ open, objetivo, initialSelection, onClose, onConfirm }: Props) => {
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const {
    aprendizajes,
    loadingAE,
    errorAE,

    criteriosByAE,
    loadingCEByAE,
    errorCEByAE,

    objetivosByCriterio,
    loadingOGByCriterio,
    errorOGByCriterio,

    expandedAE,
    setExpandedAE,
    expandedCE,
    setExpandedCE,

    fetchCEForAE,
    fetchOGForCriterio,
  } = useOATreeLoaderTP({ open, objetivoId: objetivo?.id });

  const {
    selected,
    isOGSelected,
    toggleOGSelection,
    removeOGSelection,
    clearAll,
    selectedCounts,
    canConfirm,
    resumen,
    buildSelection,
  } = useOATreeSelection({
    open,
    objetivo,
    initialSelection,
    aprendizajes,
    criteriosByAE,
    objetivosByCriterio,
  });

  const handleOpenReview = () => {
    if (!canConfirm) return;
    setIsReviewOpen(true);
  };

  const handleBackToSelection = () => setIsReviewOpen(false);

  const handleFinish = () => {
    if (!canConfirm) return;
    onConfirm(buildSelection());
    setIsReviewOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !isReviewOpen}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
      >
        {/* Primer asección */}
        <ObjectiveSelectHeader
          objetivo={{ codigo: objetivo.codigo, objetivo: objetivo.objetivo }}
          onClose={onClose}
        />

        <DialogContent sx={{ fontFamily: "Montserrat", pt: 1.5 }}>
          {/* Cointenedor 2 columnas */}
          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <Grid container>
              {/* LEFT: Arbol de datos */}
              <ObjectiveSelectLeftTree
                aprendizajes={aprendizajes}
                loadingAE={loadingAE}
                errorAE={errorAE}
                criteriosByAE={criteriosByAE}
                loadingCEByAE={loadingCEByAE}
                errorCEByAE={errorCEByAE}
                objetivosByCriterio={objetivosByCriterio}
                loadingOGByCriterio={loadingOGByCriterio}
                errorOGByCriterio={errorOGByCriterio}
                expandedAE={expandedAE}
                expandedCE={expandedCE}
                onToggleAE={(aeId, next) => setExpandedAE((prev) => ({ ...prev, [aeId]: next }))}
                onToggleCE={(ceId, next) => setExpandedCE((prev) => ({ ...prev, [ceId]: next }))}
                fetchCEForAE={fetchCEForAE}
                fetchOGForCriterio={fetchOGForCriterio}
                isOGSelected={isOGSelected}
                toggleOGSelection={toggleOGSelection}
              />

              {/* RIGHT: Resumen selección del usuario */}
              <ObjectiveSelectRightPanel
                selected={selected}
                canConfirm={canConfirm}
                selectedAECount={selectedCounts.aeCount}
                onClearAll={clearAll}
                onRemoveOG={removeOGSelection}
              />
            </Grid>
          </Box>

          {/* Footer */}
          <ObjectiveSelectFooter canConfirm={canConfirm} onCancel={onClose} onContinue={handleOpenReview} />
        </DialogContent>
      </Dialog>

      {/* Modal de resumen */}
      <ObjectiveSelectionReviewModal
        open={open && isReviewOpen}
        objetivo={{ codigo: objetivo.codigo, objetivo: objetivo.objetivo }}
        resumen={{
          aprendizajesTexto: resumen.aprendizajesTexto,
          criteriosTexto: resumen.criteriosTexto,
          objetivosGenericosTexto: resumen.objetivosGenericosTexto,
        }}
        onBack={handleBackToSelection}
        onFinish={handleFinish}
      />
    </>
  );
};

export default ObjectiveSelectModal;
